// ── Marker visibility + sale tags ──
// Extracted verbatim from MapCanvas.tsx. Two adjacent effects kept in the same
// order inside one hook: (1) toggling plot/sale/quest/avatar marker visibility
// when the "show plots" switch flips, without a full rebuild; (2) rebuilding
// the "for sale" plaques and repositioning owner avatars when the marketplace
// listings change (this also covers listings arriving AFTER the houses render).
// The plaque itself is built by the shared createSaleTag factory — it used to be
// duplicated inline here and drifted from the copy in useMapObjectsRender.
//
// Bodies are byte-identical to the originals; every closure dependency is a
// parameter under its ORIGINAL name.
import { useEffect, type MutableRefObject } from 'react';
import { createSaleTag } from './render/mapMarkers';

export interface UseMarkerVisibilityParams {
  /** Effect deps — by value. */
  showPlots: boolean;
  markersReady: boolean;
  isPixiAppReady: boolean;
  listedHouseIdsKey: string;
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
}

export function useMarkerVisibility({
  showPlots,
  markersReady,
  isPixiAppReady,
  listedHouseIdsKey,
  sceneRef,
}: UseMarkerVisibilityParams): void {
  // ── Toggle marker visibility without full rebuild ──
  useEffect(() => {
    sceneRef.current.markers.plots.forEach((marker) => {
      marker.visible = showPlots;
    });
    sceneRef.current.markers.sales.forEach((marker) => {
      marker.visible = showPlots;
    });
    sceneRef.current.markers.quests.forEach((marker) => {
      marker.visible = showPlots;
    });
    sceneRef.current.markers.avatars.forEach((marker) => {
      marker.visible = showPlots;
    });
  }, [showPlots, sceneRef]);

  // ── Listing-change update: rebuild sale tags + avatar positions when listings change ──
  // Also handles the case where listings load AFTER initial house rendering.
  useEffect(() => {
    if (!markersReady || !sceneRef.current.layers.mapObjects || !isPixiAppReady) return;

    const currentListingsByHouseId = sceneRef.current.data.listingsByHouseId;
    const currentHouses = sceneRef.current.data.mapObjects.filter(obj => obj.type === 'house' && obj.owner_id !== null);

    // Rebuild sale markers
    sceneRef.current.markers.sales.forEach((m) => { if (m.parent) m.parent.removeChild(m); m.destroy(); });
    sceneRef.current.markers.sales.clear();

    for (const house of currentHouses) {
      if (!sceneRef.current.layers.mapObjects) break;
      const listing = currentListingsByHouseId.get(house.id);
      if (!listing) continue;
      const houseMarker = sceneRef.current.markers.realEstate.get(house.id);
      if (!houseMarker || houseMarker.destroyed) continue;

      // Built by the shared factory, not inline: this rebuild is the path that
      // actually runs most of the time (listings arrive after the houses render), so
      // an inline copy here silently overrides whatever useMapObjectsRender drew.
      // That is how the plaque kept showing the full price at full opacity.
      const container = createSaleTag(house, house.y - 70, {
        showPlots: sceneRef.current.flags.showPlots,
      });
      sceneRef.current.layers.mapObjects.addChild(container);
      sceneRef.current.markers.sales.set(house.id, container);

      // Reposition avatar above sale tag
      const avatarMarker = sceneRef.current.markers.avatars.get(house.id);
      if (avatarMarker && !avatarMarker.destroyed) {
        avatarMarker.position.set(house.x, house.y - 115);
      }
    }

    // Reset avatar position for houses that lost their sale tag
    sceneRef.current.markers.avatars.forEach((marker, houseId) => {
      if (!currentListingsByHouseId.has(houseId) && !marker.destroyed) {
        const house = currentHouses.find(h => h.id === houseId);
        if (house) marker.position.set(house.x, house.y - 80);
      }
    });
    // isPixiAppReady / sceneRef: markersReady + listedHouseIdsKey — тригер перебудови тегів;
    // sceneRef.current читається імперативно (PIXI bag), isPixiAppReady уже gate у markersReady path.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listing key + markersReady only
  }, [listedHouseIdsKey, markersReady]);
}
